/**
 * Screenshot service — captures viewport and canvas screenshots for bug reports.
 *
 * Note: html2canvas renders a DOM snapshot and cannot capture WebGL or 2D canvas
 * contents (those will appear blank). Use captureCanvas() to get the raw canvas
 * pixels via native toDataURL().
 */

// html2canvas (~50KB gzip) is only needed when a user actually files a bug
// report, so it's imported lazily inside captureViewport() rather than pulled
// into the app entry chunk paid by every login. (#260)

const JPEG_QUALITY = 0.7;
const DATA_URL_PREFIX_RE = /^data:[^;]+;base64,/;

/**
 * Maximum base64 screenshot length accepted by POST /api/ghe/create-issue.
 * Mirrors `GHE_ISSUE_LIMITS.maxScreenshotLength` in
 * api/src/lib/request-validation.ts (the webapp cannot import from api/,
 * so the value is duplicated here with this comment as the link).
 */
export const MAX_SCREENSHOT_BASE64_LENGTH = 500_000;

/**
 * Feedback UI is portaled to <body>, so a full-viewport html2canvas capture
 * would otherwise photograph the bug report dialog itself instead of the
 * issue behind it ("the issue disappears / is negated by the bug report
 * interface"). These selectors are hidden (visibility, restored afterwards)
 * for the duration of the capture.
 */
const HIDE_DURING_CAPTURE_SELECTOR =
  '[role="dialog"], [class*="fui-Dialog"], sentry-feedback, .br-overlay';

/**
 * Captures the full viewport as a JPEG screenshot using html2canvas.
 * Returns the raw base64 string (no data-URI prefix), or null on failure.
 *
 * Feedback dialogs (Fluent UI wizard, Sentry feedback form, bug-reporter
 * overlay) are temporarily hidden so the capture shows the app — the thing
 * being reported — not the report UI.
 */
export async function captureViewport(): Promise<string | null> {
  const restored: Array<{ el: HTMLElement; visibility: string }> = [];
  try {
    document
      .querySelectorAll<HTMLElement>(HIDE_DURING_CAPTURE_SELECTOR)
      .forEach((el) => {
        restored.push({ el, visibility: el.style.visibility });
        el.style.visibility = 'hidden';
      });

    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      logging: false,
      scale: 1,
    });
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    return dataUrl.replace(DATA_URL_PREFIX_RE, '');
  } catch (err) {
    console.warn('screenshotService: captureViewport failed', err);
    return null;
  } finally {
    restored.forEach(({ el, visibility }) => {
      el.style.visibility = visibility;
    });
  }
}

/**
 * Synchronously captures the largest <canvas> element on the page as a JPEG.
 * Returns the raw base64 string (no data-URI prefix), or null if no canvas is
 * found, the canvas has zero dimensions, or an error occurs.
 */
export function captureCanvas(): string | null {
  try {
    const canvases = Array.from(document.querySelectorAll('canvas'));
    if (canvases.length === 0) return null;

    // Pick the canvas with the greatest pixel area
    const largest = canvases.reduce((best, current) => {
      return current.width * current.height > best.width * best.height
        ? current
        : best;
    });

    if (largest.width === 0 || largest.height === 0) return null;

    const dataUrl = largest.toDataURL('image/jpeg', JPEG_QUALITY);
    return dataUrl.replace(DATA_URL_PREFIX_RE, '');
  } catch (err) {
    console.warn('screenshotService: captureCanvas failed', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Image attachment helpers (BUG A — attach an existing screenshot)
// ---------------------------------------------------------------------------

/**
 * Downscales a JPEG data-URL until its base64 payload fits within
 * `maxBase64Chars` (the GHE create-issue screenshot limit). Shrinks the
 * longest side by 25% per iteration, then lowers JPEG quality as a final
 * lever. Returns the raw base64 string (no data-URI prefix), or null when
 * the image cannot be decoded even at minimum size.
 */
export async function downscaleImageToLimit(
  dataUrl: string,
  maxBase64Chars: number = MAX_SCREENSHOT_BASE64_LENGTH,
): Promise<string | null> {
  let base64 = dataUrl.replace(DATA_URL_PREFIX_RE, '');
  if (base64.length <= maxBase64Chars) return base64;

  try {
    const image = await loadImage(dataUrl);

    let { width, height } = image;
    let quality = JPEG_QUALITY;

    while (base64.length > maxBase64Chars && (width > 64 || height > 64 || quality > 0.3)) {
      if (width > 64 || height > 64) {
        width = Math.max(1, Math.round(width * 0.75));
        height = Math.max(1, Math.round(height * 0.75));
      } else {
        // Floor reached on dimensions — drop quality instead.
        quality = Math.max(0.3, quality - 0.1);
      }
      base64 = drawToBase64(image, width, height, quality);
    }

    return base64.length <= maxBase64Chars ? base64 : null;
  } catch (err) {
    console.warn('screenshotService: downscaleImageToLimit failed', err);
    return null;
  }
}

/**
 * Reads an image file picked by the user and returns it as a JPEG base64
 * string (no data-URI prefix) that satisfies the GHE screenshot limit
 * (downscaled client-side when needed). Rejects non-image files and read
 * failures.
 */
export async function readImageFileAsBase64(
  file: File,
  maxBase64Chars: number = MAX_SCREENSHOT_BASE64_LENGTH,
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files can be attached.');
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the selected file.'));
    reader.readAsDataURL(file);
  });

  const base64 = await downscaleImageToLimit(dataUrl, maxBase64Chars);
  if (base64 === null) {
    throw new Error('The image is too large to attach even after downscaling.');
  }
  return base64;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not decode the selected image.'));
    image.src = src;
  });
}

function drawToBase64(
  image: HTMLImageElement,
  width: number,
  height: number,
  quality: number,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');
  ctx.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality).replace(DATA_URL_PREFIX_RE, '');
}
